import { Box, Center, Loader, Tabs } from '@mantine/core'
import {
  IconActivity,
  IconDeviceDesktop,
  IconTerminal2,
  IconVideo
} from '@tabler/icons-react'
import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { ErrorWithReload } from '@/components/ui-shared/ErrorWithReload/ErrorWithReload'
import { ListItemDrawer } from '@/components/ui-shared/ListItemDrawer/ListItemDrawer'
import { useGetDeviceDetails } from '@/queries/deviceQueries'
import { Deployment, InputSourceType } from '@/types/deployment'
import { getDevicesCameras } from '@/utils/device/getDeviceAttr'
import { DeploymentAppLogsTab } from './DeploymentAppLogsTab'
import { DeploymentCameraTab } from './DeploymentCameraTab'
import { DeploymentDeviceTab } from './DeploymentDeviceTab'
import { DeploymentObservabilityTab } from './DeploymentObservabilityTab'

type DeploymentDetailsDrawerProps = {
  opened: boolean
  deployment: Deployment
  onClose: () => void
}

export const DeploymentDetailsDrawer = ({
  opened,
  deployment,
  onClose
}: DeploymentDetailsDrawerProps) => {
  const { settings_payload } = deployment

  const {
    data: device,
    isLoading,
    isError,
    refetch
  } = useGetDeviceDetails(
    settings_payload?.device_id ?? '',
    opened && !!settings_payload
  )

  const camera = useMemo(() => {
    if (
      !device ||
      !settings_payload ||
      settings_payload.input_source_type !== InputSourceType.Cameras
    ) {
      return null
    }
    return (
      getDevicesCameras(device).find(
        (c) => c.camera_id === settings_payload.stream_id
      ) ?? null
    )
  }, [device, settings_payload])

  return (
    <ListItemDrawer
      opened={opened}
      title={deployment.application_name}
      size="60%"
      onClose={onClose}
    >
      <Box maw={1100}>
        {isLoading && (
          <Center mih={240}>
            <Loader size="lg" />
          </Center>
        )}

        {isError && (
          <ErrorWithReload onReload={() => void refetch()}>
            <FormattedMessage id="devices.data.error" />
          </ErrorWithReload>
        )}

        {!isLoading && !isError && (
          <Tabs
            variant="outline"
            defaultValue={device && settings_payload ? 'camera' : 'app-logs'}
            keepMounted={false}
          >
            <Tabs.List>
              {device && settings_payload && (
                <>
                  <Tabs.Tab
                    value="camera"
                    leftSection={<IconVideo size={16} />}
                  >
                    <FormattedMessage id="deployments.details.tabs.camera" />
                  </Tabs.Tab>

                  <Tabs.Tab
                    value="device"
                    leftSection={<IconDeviceDesktop size={16} />}
                  >
                    <FormattedMessage id="deployments.details.tabs.device" />
                  </Tabs.Tab>

                  <Tabs.Tab
                    value="observability"
                    leftSection={<IconActivity size={16} />}
                  >
                    <FormattedMessage id="deployments.details.tabs.observability" />
                  </Tabs.Tab>
                </>
              )}

              <Tabs.Tab
                value="app-logs"
                leftSection={<IconTerminal2 size={16} />}
              >
                <FormattedMessage id="deployments.details.tabs.appLogs" />
              </Tabs.Tab>
            </Tabs.List>

            {device && settings_payload && (
              <>
                <Tabs.Panel value="camera" pt="md">
                  <DeploymentCameraTab
                    camera={camera}
                    inputSourceType={settings_payload.input_source_type}
                    device={device}
                    appId={deployment.application_id}
                    onRefresh={() => void refetch()}
                  />
                </Tabs.Panel>

                <Tabs.Panel value="device" pt="md">
                  <DeploymentDeviceTab
                    device={device}
                    onRefresh={() => void refetch()}
                  />
                </Tabs.Panel>

                <Tabs.Panel value="observability" pt="md">
                  <DeploymentObservabilityTab
                    device={device}
                    activeGpuIndexes={[
                      deployment.gpu?.index ?? deployment.gpu_index ?? 0
                    ]}
                    appName={deployment.application_name}
                  />
                </Tabs.Panel>
              </>
            )}

            <Tabs.Panel value="app-logs" pt="md">
              <DeploymentAppLogsTab
                scope={{ kind: 'deployment', deploymentId: deployment.id }}
              />
            </Tabs.Panel>
          </Tabs>
        )}
      </Box>
    </ListItemDrawer>
  )
}
